import React, { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import ButtonLoader from "../../components/button/ButtonLoader";
import {
  Delete_Partnership_API,
  getPartnershipDataAPI,
  getSocialMediaAPI,
} from "../../API/PartnershipAPI/PartnershipAPI";
import { BsThreeDotsVertical } from "react-icons/bs";
import AddPart from "../../components/Modal/AddPart";
import Wrapper from "../../components/wrapper/Wrapper";
import SimplePagination from "../../components/Pagination/SimplePagination";
import {
  FiPlus,
  FiEdit3,
  FiTrash2,
  FiExternalLink,
  FiDollarSign,
  FiCalendar,
  FiHome,
  FiTarget,
  FiGlobe,
} from "react-icons/fi";

const Partnership = () => {
  const queryClient = useQueryClient();
  const [pageNumber, setPageNumber] = useState(1);
  const [isOpen, setIsOpen] = useState(false);
  const [menuState, setMenuState] = useState({ loader: false, rowId: null });
  const [editMode, setEditMode] = useState(null);
  const [limit, setLimit] = useState(10);
  const [expandedLinks, setExpandedLinks] = useState(new Set());

  const {
    data: PartData,
    isLoading: isPartLoading,
    error: partError,
    refetch,
  } = useQuery({
    queryKey: ["partnership", pageNumber, limit],
    queryFn: () => getPartnershipDataAPI({ pageNumber, limit }),
    staleTime: 0,
    keepPreviousData: true,
  });

  useEffect(() => {
    const handleOutsideClick = (e) => {
      // Check if click is on the three dots button
      const clickedElement = e.target.closest("#BsThreeDotsVertical");
      if (clickedElement) {
        const value = clickedElement.getAttribute("value");
        if (
          menuState.rowId &&
          value &&
          menuState.rowId.toString() === value.toString()
        ) {
          setMenuState({ loader: false, rowId: null });
        } else {
          setMenuState(() => ({
            loader: false,
            rowId: value,
          }));
        }
      }
      // Check if click is on edit or delete buttons - don't close menu
      else if (e.target.closest("[data-action-button]")) {
        // Don't close menu if clicking on action buttons
        return;
      }
      // Check if click is outside any menu - close all menus
      else if (!e.target.closest("[data-actions-menu]")) {
        setMenuState({ loader: false, rowId: null });
      }
    };

    document.addEventListener("mousedown", handleOutsideClick);

    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
    };
  }, [menuState.rowId]); // Add menuState.rowId to dependencies

  // Debug useEffect to monitor state changes
  useEffect(() => {
    console.log("State changed - isOpen:", isOpen, "editMode:", editMode);
  }, [isOpen, editMode]);

  const handleLimitChange = async (newLimit) => {
    setLimit(newLimit);
    setPageNumber(1);
    refetch();
  };

  const {
    data: socialMediaData,
    isLoading: isSocialLoading,
    error: socialError,
  } = useQuery({
    queryKey: ["socialMedia"],
    queryFn: () => getSocialMediaAPI(),
  });

  if (partError || socialError) {
    return (
      <div className="text-center text-red-500">
        Error: {partError?.message || socialError?.message}
      </div>
    );
  }

  function handleToggle() {
    setEditMode(null); // Reset edit mode for new partnership
    setIsOpen(true);
  }

  function handleEditToggle(partnershipId) {
    console.log("handleEditToggle called with:", partnershipId);
    console.log("Setting editMode to:", partnershipId);
    console.log("Setting isOpen to: true");
    setEditMode(partnershipId); // Set edit mode with partnership ID
    setIsOpen(true);
  }

  const toggleMenu = (rowId) => {
    setMenuState((prevState) => ({
      ...prevState,
      rowId: rowId,
    }));
  };

  const toggleLinkExpansion = (linkId) => {
    setExpandedLinks((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(linkId)) {
        newSet.delete(linkId);
      } else {
        newSet.add(linkId);
      }
      return newSet;
    });
  };

  const truncateUrl = (url, maxLength = 25) => {
    if (url.length <= maxLength) return url;
    return url.substring(0, maxLength) + "...";
  };

  const handleDeletePart = async (userPartId) => {
    console.log("handleDeletePart called with:", userPartId);
    setMenuState((prevState) => ({
      ...prevState,
      loader: true,
      rowId: userPartId,
    }));

    try {
      await Delete_Partnership_API(userPartId);
      queryClient.setQueryData(
        ["partnership", pageNumber, limit],
        (oldData) => {
          if (!oldData) return oldData;
          return {
            ...oldData,
            data: {
              ...oldData.data,
              data: oldData.data.data.filter(
                (item) => item._id.toString() !== userPartId.toString()
              ),
            },
          };
        }
      );
    } catch (error) {
      console.error("Failed to delete partnership:", error);
    } finally {
      setMenuState({ loader: false, rowId: null });
    }
  };

  return (
    <>
      <Wrapper>
        {/* Header Section */}
        <div className="mb-8">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className="text-2xl font-semibold text-gray-900">
                Partnerships
              </h1>
              <p className="text-gray-600 mt-1">
                Manage business partnerships and collaborations
              </p>
            </div>
            <button
              onClick={handleToggle}
              className="inline-flex items-center gap-2 px-4 py-2 bg-[--primary] text-white rounded-lg hover:bg-[--primary]/90 transition-colors font-medium"
            >
              <FiPlus className="w-4 h-4" />
              Add Partnership
            </button>
          </div>
        </div>
        {/* Partnerships Table */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          {isPartLoading || isSocialLoading ? (
            <div className="flex justify-center items-center py-12">
              <ButtonLoader />
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full table-fixed">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-48">
                      Partnership
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-32">
                      Company
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-48">
                      Website
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-80">
                      Deliverables
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-32">
                      Platform
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-24">
                      Amount
                    </th>
                    <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-28">
                      Deadline
                    </th>
                    <th className="px-6 py-4 text-center text-xs font-medium text-gray-500 uppercase tracking-wider w-20">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {PartData.data.data?.map((rowData, index) => {
                    return (
                      <tr
                        key={rowData._id}
                        className="hover:bg-gray-50 transition-colors"
                      >
                        {/* Partnership Column */}
                        <td className="px-6 py-4 w-48">
                          <div className="flex items-center gap-3">
                            <div className="flex-shrink-0">
                              <img
                                src={rowData.companyLogo}
                                alt={rowData.title}
                                className="w-12 h-12 rounded-lg border border-gray-200 object-cover"
                              />
                            </div>
                            <div className="min-w-0 flex-1">
                              <h3 className="text-sm font-medium text-gray-900">
                                {rowData.title}
                              </h3>
                              <p className="text-sm text-gray-500">
                                Partnership
                              </p>
                            </div>
                          </div>
                        </td>

                        {/* Company Column */}
                        <td className="px-6 py-4 w-32">
                          <div className="flex items-center gap-2">
                            <FiHome className="w-4 h-4 text-gray-400" />
                            <span className="text-sm text-gray-900">
                              {rowData.partnerShipCompanyName}
                            </span>
                          </div>
                        </td>

                        {/* Website Column */}
                        <td className="px-6 py-4 w-48">
                          <div className="flex items-center gap-2">
                            <FiGlobe className="w-4 h-4 text-gray-400 flex-shrink-0" />
                            <div className="flex items-center gap-1 min-w-0">
                              <span
                                onClick={() => toggleLinkExpansion(rowData._id)}
                                className="text-xs text-[--primary] hover:text-[--primary]/80 transition-colors cursor-pointer truncate"
                                title={
                                  expandedLinks.has(rowData._id)
                                    ? "Click to collapse"
                                    : "Click to expand"
                                }
                              >
                                {expandedLinks.has(rowData._id)
                                  ? rowData.websiteLink
                                  : truncateUrl(rowData.websiteLink)}
                              </span>
                              <a
                                href={rowData.websiteLink}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-[--primary] hover:text-[--primary]/80 transition-colors flex-shrink-0"
                                title="Open in new tab"
                              >
                                <FiExternalLink className="w-2.5 h-2.5" />
                              </a>
                            </div>
                          </div>
                        </td>

                        {/* Deliverables Column */}
                        <td className="px-6 py-4 w-80">
                          <div className="flex items-start gap-2">
                            <FiTarget className="w-4 h-4 text-gray-400 flex-shrink-0 mt-0.5" />
                            <span className="text-sm text-gray-900 break-words leading-relaxed">
                              {rowData.deliverables}
                            </span>
                          </div>
                        </td>

                        {/* Platform Column */}
                        <td className="px-6 py-4 w-32">
                          <div className="flex flex-wrap gap-1">
                            {rowData.platForm?.map((platform, idx) => (
                              <span
                                key={idx}
                                className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
                              >
                                {platform}
                              </span>
                            ))}
                          </div>
                        </td>

                        {/* Amount Column */}
                        <td className="px-6 py-4 w-24">
                          <div className="flex items-center gap-2">
                            <FiDollarSign className="w-4 h-4 text-gray-400" />
                            <span className="text-sm font-semibold text-gray-900">
                              {rowData.amount}
                            </span>
                          </div>
                        </td>

                        {/* Deadline Column */}
                        <td className="px-6 py-4 w-28">
                          <div className="flex items-center gap-2">
                            <FiCalendar className="w-4 h-4 text-gray-400" />
                            <span className="text-sm text-gray-900">
                              {new Date(rowData.deadline).toLocaleDateString()}
                            </span>
                          </div>
                        </td>
                        {/* Actions Column */}
                        <td className="px-6 py-4 text-center w-20">
                          <div className="relative" data-actions-menu>
                            <button
                              id="BsThreeDotsVertical"
                              value={rowData._id}
                              onClick={(e) => {
                                e.stopPropagation();
                                toggleMenu(rowData._id);
                              }}
                              className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                            >
                              <BsThreeDotsVertical className="w-4 h-4" />
                            </button>

                            {menuState.rowId === rowData._id && (
                              <div className="absolute right-0 top-8 bg-white border border-gray-200 shadow-lg rounded-lg py-1 z-10 min-w-[140px]">
                                <button
                                  data-action-button="edit"
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    console.log(
                                      "Edit button clicked for:",
                                      rowData._id
                                    );
                                    handleEditToggle(rowData._id);
                                    setMenuState({
                                      loader: false,
                                      rowId: null,
                                    }); // Close menu after edit
                                  }}
                                  className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 flex items-center gap-2"
                                >
                                  <FiEdit3 className="w-4 h-4" />
                                  Edit
                                </button>
                                <button
                                  data-action-button="delete"
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    console.log(
                                      "Delete button clicked for:",
                                      rowData._id
                                    );
                                    handleDeletePart(rowData._id);
                                  }}
                                  className="w-full px-4 py-2 text-left text-sm text-red-600 hover:bg-red-50 flex items-center gap-2"
                                  disabled={
                                    menuState.loader &&
                                    menuState.rowId === rowData._id
                                  }
                                >
                                  {menuState.loader &&
                                  menuState.rowId === rowData._id ? (
                                    <div className="w-4 h-4 border-2 border-red-600 border-t-transparent rounded-full animate-spin"></div>
                                  ) : (
                                    <FiTrash2 className="w-4 h-4" />
                                  )}
                                  Delete
                                </button>
                              </div>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {/* Empty State */}
          {!isPartLoading &&
            !isSocialLoading &&
            PartData?.data?.data?.length === 0 && (
              <div className="text-center py-12">
                <div className="mx-auto w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                  <FiHome className="w-12 h-12 text-gray-400" />
                </div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  No partnerships found
                </h3>
                <p className="text-gray-500 mb-6">
                  Get started by creating your first partnership.
                </p>
                <button
                  onClick={handleToggle}
                  className="inline-flex items-center gap-2 px-4 py-2 bg-[--primary] text-white rounded-lg hover:bg-[--primary]/90 transition-colors font-medium"
                >
                  <FiPlus className="w-4 h-4" />
                  Create Partnership
                </button>
              </div>
            )}

          {PartData?.data?.data?.length > 0 && (
            <SimplePagination
              totalPages={PartData?.data?.paginationInfo?.totalPages}
              currentPage={PartData?.data.paginationInfo.currentPage}
              onPageChange={setPageNumber}
              limit={limit}
              onLimitChange={handleLimitChange}
              totalItems={PartData?.data.paginationInfo.totalItems}
            />
          )}
        </div>
      </Wrapper>
      <AddPart
        pageNumber={pageNumber}
        queryClient={queryClient}
        isOpen={isOpen}
        setIsOpen={(open) => {
          console.log("Modal setIsOpen called with:", open);
          setIsOpen(open);
          if (!open) {
            console.log("Modal closing, resetting editMode");
            setEditMode(null); // Reset edit mode when modal closes
          }
        }}
        socialMediaData={socialMediaData}
        limit={limit}
        PartData={PartData}
        isEditMode={editMode} // Pass edit mode state
      />
    </>
  );
};

export default Partnership;
